import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import html2canvas from 'html2canvas';
import CenteredLoadingSpinner from '../../components/CenteredLoadingSpinner';
import DataSourceAttribution from '../../components/DataSourceAttribution';
import type { SnackbarVariant } from '../../components/Snackbar';
import { adminApi, isUnauthorized } from '../../services/adminApi';
import stocksList from '../../data/stocksList.json';
import {
  fetchBalanceSheetTTM,
  fetchCashFlowStatementTTM,
  fetchHistoricalPrices,
  fetchIncomeStatementTTM,
  fetchIncomeStatementTTMSeries,
  fetchKeyMetrics,
  fetchKeyMetricsTTM,
  fetchStockQuote,
} from '../../services/stockApi';
import {
  fetchFiscalCompanyDescription,
  fetchFiscalMarketCapRatios,
  fetchFiscalStandardizedBalanceSheetTtm,
  fetchFiscalStandardizedCashFlowStatementTtm,
  fetchFiscalStandardizedIncomeStatementTtm,
  fetchFiscalStandardizedMetricsList,
  fetchFiscalRoicRatios,
} from '../../services/fiscalApi';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import SummaryNewNote from './SummaryNewNote';
import {
  getMarketSpecForExchange,
  getNextMarketOpenUtc,
  getZonedParts,
  type MarketSpec,
  zonedTimeToUtc,
} from '../../utils/marketTime';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler,
  type Plugin,
} from 'chart.js';
import 'chartjs-adapter-date-fns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler
);

type SummaryCrosshairState = {
  mouseX: number | null;
  mouseY: number | null;
};

const summaryCrosshairState = new WeakMap<any, SummaryCrosshairState>();
const getSummaryCrosshairState = (chart: any): SummaryCrosshairState => {
  const existing = summaryCrosshairState.get(chart);
  if (existing) return existing;
  const next: SummaryCrosshairState = { mouseX: null, mouseY: null };
  summaryCrosshairState.set(chart, next);
  return next;
};

// Crosshair for Research → Summary (simple dashed guide lines on hover).
// Opt-in via `options.plugins.summaryCrosshair.enabled = true`.
const summaryCrosshairPlugin: Plugin<'line'> = {
  id: 'summary-crosshair',
  afterInit(chart) {
    const canvas = (chart as any)?.canvas as HTMLCanvasElement | undefined;
    if (!canvas || typeof canvas.addEventListener !== 'function') return;

    const handler = () => {
      const s = getSummaryCrosshairState(chart);
      s.mouseX = null;
      s.mouseY = null;
      try {
        chart.draw();
      } catch {
        // ignore
      }
    };

    (chart as any).$summaryCrosshairLeaveHandler = handler;
    canvas.addEventListener('mouseleave', handler);
    canvas.addEventListener('pointerleave', handler);
    canvas.addEventListener('mouseout', handler);
  },
  beforeDestroy(chart) {
    const canvas = (chart as any)?.canvas as HTMLCanvasElement | undefined;
    const handler = (chart as any).$summaryCrosshairLeaveHandler as (() => void) | undefined;
    if (canvas && handler) {
      canvas.removeEventListener('mouseleave', handler);
      canvas.removeEventListener('pointerleave', handler);
      canvas.removeEventListener('mouseout', handler);
    }
    delete (chart as any).$summaryCrosshairLeaveHandler;
  },
  afterEvent: (chart, args) => {
    const enabled = Boolean((chart.options as any)?.plugins?.summaryCrosshair?.enabled);
    if (!enabled) return;

    const s = getSummaryCrosshairState(chart);
    const { event } = args;
    const area = (chart as any)?.chartArea;
    if (!area) return;

    if (event.type === 'mousemove') {
      const ev = event as any;
      const x = ev?.x;
      const y = ev?.y;
      if (typeof x !== 'number' || typeof y !== 'number') return;

      // Require Y in plot area; allow X anywhere and clamp later.
      if (y >= area.top && y <= area.bottom) {
        s.mouseX = x;
        s.mouseY = y;
      } else {
        s.mouseX = null;
        s.mouseY = null;
      }

      chart.draw();
      return;
    }

    if (event.type === 'mouseout') {
      s.mouseX = null;
      s.mouseY = null;
      chart.draw();
    }
  },
  afterDatasetsDraw: (chart) => {
    const enabled = Boolean((chart.options as any)?.plugins?.summaryCrosshair?.enabled);
    if (!enabled) return;

    const s = getSummaryCrosshairState(chart);
    if (s.mouseX === null || s.mouseY === null) return;

    const ctx = (chart as any)?.ctx as CanvasRenderingContext2D | undefined;
    const area = (chart as any)?.chartArea;
    if (!ctx || !area) return;

    const { left, right, top, bottom } = area;
    const xPos = Math.max(left, Math.min(right, s.mouseX));
    const yPos = Math.max(top, Math.min(bottom, s.mouseY));

    const cfg = ((chart.options as any)?.plugins?.summaryCrosshair ?? {}) as { color?: string; dash?: number[]; width?: number };
    const color = cfg.color ?? 'rgba(148,163,184,0.55)';
    const dash = Array.isArray(cfg.dash) ? cfg.dash : [5, 5];
    const width = Number.isFinite(Number(cfg.width)) ? Number(cfg.width) : 1;

    ctx.save();
    ctx.setLineDash(dash);
    ctx.strokeStyle = color;
    ctx.lineWidth = width;

    ctx.beginPath();
    ctx.moveTo(xPos, top);
    ctx.lineTo(xPos, bottom);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(left, yPos);
    ctx.lineTo(right, yPos);
    ctx.stroke();

    ctx.restore();
  },
};

interface ResearchSummaryScreenProps {
  isDarkMode: boolean;
  selectedStock: string;
  isAdmin?: boolean;
  authToken?: string | null;
  authUserId?: number | null;
  onUnauthorized?: () => void;
  onNotify?: (message: string, variant?: SnackbarVariant) => void;
}

interface CompanyMetrics {
  // Balance Sheet & Liquidity
  cash: number | null;
  longTermDebt: number | null;
  cashToDebtRatio: number | null;
  fcfToDebtRatio: number | null;
  
  // Profitability Margins
  grossMargin: number | null;
  operatingMargin: number | null;
  netMargin: number | null;
  fcfMargin: number | null;
  
  // Valuation
  marketCap: number | null;
  peRatio: number | null;
  priceToOCF: number | null;
  priceToFCF: number | null;
  volume: number | null;
  roic: number | null;
}

type StockListItem = {
  symbol: string;
  name?: string;
  exchange?: string;
  category?: string;
};

const ResearchSummaryScreen: React.FC<ResearchSummaryScreenProps> = ({
  isDarkMode,
  selectedStock,
  isAdmin,
  authToken,
  authUserId,
  onUnauthorized,
  onNotify,
}) => {
  const stockPriceChartSectionRef = useRef<HTMLDivElement | null>(null);
  const balanceSheetLiquiditySectionRef = useRef<HTMLDivElement | null>(null);
  const [savingBookmark, setSavingBookmark] = useState(false);
  const [didBookmark, setDidBookmark] = useState(false);

  const [savingBalanceSheetBookmark, setSavingBalanceSheetBookmark] = useState(false);
  const [didBalanceSheetBookmark, setDidBalanceSheetBookmark] = useState(false);

  const [bookmarkFlashById, setBookmarkFlashById] = useState<Record<string, boolean>>({});
  const bookmarkFlashTimeoutsRef = useRef<Record<string, number>>({});

  useEffect(() => {
    const timeouts = bookmarkFlashTimeoutsRef.current;
    return () => {
      Object.keys(timeouts).forEach((k) => {
        try {
          window.clearTimeout(timeouts[k]);
        } catch {
          // ignore
        }
      });
    };
  }, []);

  const triggerBookmarkFlash = (id: 'stock-price' | 'balance-sheet') => {
    setBookmarkFlashById((prev) => ({ ...prev, [id]: true }));

    const existing = bookmarkFlashTimeoutsRef.current[id];
    if (existing) {
      try {
        window.clearTimeout(existing);
      } catch {
        // ignore
      }
    }

    bookmarkFlashTimeoutsRef.current[id] = window.setTimeout(() => {
      setBookmarkFlashById((prev) => {
        if (!prev[id]) return prev;
        return { ...prev, [id]: false };
      });
    }, 30_000);
  };


  const [metrics, setMetrics] = useState<CompanyMetrics>({
    cash: null,
    longTermDebt: null,
    cashToDebtRatio: null,
    fcfToDebtRatio: null,
    grossMargin: null,
    operatingMargin: null,
    netMargin: null,
    fcfMargin: null,
    marketCap: null,
    peRatio: null,
    priceToOCF: null,
    priceToFCF: null,
    volume: null,
    roic: null,
  });

  const [loading, setLoading] = useState(true);
  const [dataSource, setDataSource] = useState<'fmp' | 'fiscal'>('fmp');
  const [companyDescription, setCompanyDescription] = useState<string>('');
  const [allData, setAllData] = useState<any[]>([]);
  const [quoteData, setQuoteData] = useState<any | null>(null);
  const [chartData, setChartData] = useState<any>(null);
  const [chartBounds, setChartBounds] = useState<{ min: string; max: string } | null>(null);
  const [selectedTimeRange, setSelectedTimeRange] = useState('All');
  const [isModalOpen, setIsModalOpen] = useState(false);
  useEscapeKey(isModalOpen, () => {
    setIsModalOpen(false);
  });

  const canPersist = !!authToken;
  const resolvedUserId =
    typeof authUserId === 'number' &&
    Number.isFinite(authUserId) &&
    (authUserId as number) > 0
      ? (authUserId as number)
      : undefined;

  const selectedStockMeta = useMemo(() => {
    const list = stocksList as StockListItem[];
    const normalized = String(selectedStock || '').trim().toUpperCase();
    return list.find((s) => String(s?.symbol || '').trim().toUpperCase() === normalized) ?? null;
  }, [selectedStock]);

  const defaultStockSymbols = useMemo(
    () => new Set(['AAPL', 'GOOG', 'MSFT', 'AMZN', 'NVDA', 'META', 'TSLA']),
    []
  );

  useEffect(() => {
    const normalized = String(selectedStock || '').trim().toUpperCase();
    if (!normalized) return;
    if (defaultStockSymbols.has(normalized)) return;
    setSelectedTimeRange((prev) => (prev === '10Y' ? prev : '10Y'));
  }, [defaultStockSymbols, selectedStock]);

  const marketSpec: MarketSpec = useMemo(() => {
    const exchange = String(selectedStockMeta?.exchange || 'NASDAQ');
    return getMarketSpecForExchange(exchange);
  }, [selectedStockMeta?.exchange]);

  // Summary "realtime" quote: use backend quotes-cache only when logged in.
  // This avoids frequent browser -> FMP quote calls, and shares the cached price across users.
  useEffect(() => {
    let disposed = false;
    let pollTimeoutId: number | null = null;

    if (!authToken) {
      setQuoteData(null);
      return;
    }

    type ClientMarketPhase = 'PreOpen' | 'Regular' | 'OffHours';
    const PREOPEN_WINDOW_MIN = 10;

    const getClientMarketPhase = (spec: MarketSpec, now: Date): ClientMarketPhase => {
      const parts = getZonedParts(now, spec.timeZone);
      const isMarketDay = spec.openWeekdays.includes(parts.weekday);
      if (!isMarketDay) return 'OffHours';

      const currentMinutes = parts.hour * 60 + parts.minute;
      const preOpenStartMinutes = Math.max(0, spec.openMinutes - PREOPEN_WINDOW_MIN);

      if (currentMinutes >= spec.openMinutes && currentMinutes < spec.closeMinutes) return 'Regular';
      if (currentMinutes >= preOpenStartMinutes && currentMinutes < spec.openMinutes) return 'PreOpen';
      return 'OffHours';
    };

    const getClientPollMs = (phase: ClientMarketPhase): number => {
      switch (phase) {
        case 'PreOpen':
          return 2 * 60 * 1000;
        case 'Regular':
          return 5 * 60 * 1000;
        default:
          return 2 * 60 * 60 * 1000;
      }
    };

    const getNextBoundaryUtc = (spec: MarketSpec, now: Date): Date => {
      const parts = getZonedParts(now, spec.timeZone);
      const openHour = Math.floor(spec.openMinutes / 60);
      const openMinute = spec.openMinutes % 60;

      const closeHour = Math.floor(spec.closeMinutes / 60);
      const closeMinute = spec.closeMinutes % 60;

      const preOpenStartMinutes = Math.max(0, spec.openMinutes - PREOPEN_WINDOW_MIN);
      const preOpenHour = Math.floor(preOpenStartMinutes / 60);
      const preOpenMinute = preOpenStartMinutes % 60;

      const isMarketDay = spec.openWeekdays.includes(parts.weekday);
      const todayPreOpenUtc = isMarketDay
        ? zonedTimeToUtc({ year: parts.year, month: parts.month, day: parts.day, hour: preOpenHour, minute: preOpenMinute }, spec.timeZone)
        : null;
      const todayOpenUtc = isMarketDay
        ? zonedTimeToUtc({ year: parts.year, month: parts.month, day: parts.day, hour: openHour, minute: openMinute }, spec.timeZone)
        : null;
      const todayCloseUtc = isMarketDay
        ? zonedTimeToUtc({ year: parts.year, month: parts.month, day: parts.day, hour: closeHour, minute: closeMinute }, spec.timeZone)
        : null;

      if (todayPreOpenUtc && now.getTime() < todayPreOpenUtc.getTime()) return todayPreOpenUtc;
      if (todayOpenUtc && now.getTime() < todayOpenUtc.getTime()) return todayOpenUtc;
      if (todayCloseUtc && now.getTime() < todayCloseUtc.getTime()) return todayCloseUtc;

      const nextOpen = getNextMarketOpenUtc(spec, now);
      return new Date(nextOpen.getTime() - PREOPEN_WINDOW_MIN * 60 * 1000);
    };

    const fetchQuote = async () => {
      try {
        const { rows } = await adminApi.getQuotesCacheWithMeta(String(authToken), [selectedStock]);
        if (disposed) return;

        const normalized = String(selectedStock || '').trim().toUpperCase();
        const cached = (rows || []).find(r => String(r?.symbol || '').trim().toUpperCase() === normalized) ?? null;
        const price = cached?.currentPrice != null ? Number(cached.currentPrice) : NaN;

        if (Number.isFinite(price) && price > 0) {
          setQuoteData({ price });
          return;
        }

        // Cache miss: fall back to FMP quote once (logged-in only), then seed server cache.
        const quote = await fetchStockQuote(selectedStock);
        if (disposed) return;
        const fallbackPrice = Number(quote?.price);
        if (Number.isFinite(fallbackPrice) && fallbackPrice > 0) {
          setQuoteData({ price: fallbackPrice });
          void adminApi.upsertQuotesCache(String(authToken), {
            symbol: selectedStock,
            currentPrice: fallbackPrice,
            changePct: (typeof (quote as any)?.changesPercentage === 'number' ? (quote as any).changesPercentage : null),
          }).catch(() => {
            // best-effort
          });
        } else {
          setQuoteData(null);
        }
      } catch (e: any) {
        if (isUnauthorized(e)) {
          onUnauthorized?.();
          return;
        }
        setQuoteData(null);
      }
    };

    const scheduleNext = () => {
      if (disposed) return;
      const now = new Date();
      const phase = getClientMarketPhase(marketSpec, now);
      const pollMs = getClientPollMs(phase);
      const boundary = getNextBoundaryUtc(marketSpec, now);
      const untilBoundaryMs = Math.max(0, boundary.getTime() - now.getTime()) + 750;
      const delayMs = Math.max(1_000, Math.min(pollMs, untilBoundaryMs));

      pollTimeoutId = window.setTimeout(async () => {
        await fetchQuote();
        scheduleNext();
      }, delayMs);
    };

    void fetchQuote();
    scheduleNext();

    return () => {
      disposed = true;
      if (pollTimeoutId != null) window.clearTimeout(pollTimeoutId);
    };
  }, [authToken, marketSpec, onUnauthorized, selectedStock]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!canPersist) {
        setDidBookmark(false);
        return;
      }

      // Reset immediately on stock/time-range change so we don't
      // show a filled icon from the previous stock while loading.
      setDidBookmark(false);

      try {
        const title = `Stock Price (${selectedTimeRange})`;
        const items = await adminApi.listResearchDeskScreenshots(authToken as string, {
          userId: resolvedUserId,
          sourcePage: 'Summary',
          stockSymbol: selectedStock,
          title,
          limit: 10,
        });

        if (cancelled) return;
        setDidBookmark(items.length > 0);
      } catch (e: any) {
        if (isUnauthorized(e)) {
          onUnauthorized?.();
          return;
        }
        if (cancelled) return;
        setDidBookmark(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [canPersist, authToken, resolvedUserId, onUnauthorized, selectedStock, selectedTimeRange]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!canPersist) {
        setDidBalanceSheetBookmark(false);
        return;
      }

      // Reset immediately on stock change so we don't show a filled icon
      // from the previous stock while loading.
      setDidBalanceSheetBookmark(false);

      try {
        const title = 'Balance Sheet & Liquidity';
        const items = await adminApi.listResearchDeskScreenshots(authToken as string, {
          userId: resolvedUserId,
          sourcePage: 'Summary',
          stockSymbol: selectedStock,
          title,
          limit: 10,
        });

        if (cancelled) return;
        setDidBalanceSheetBookmark(items.length > 0);
      } catch (e: any) {
        if (isUnauthorized(e)) {
          onUnauthorized?.();
          return;
        }
        if (cancelled) return;
        setDidBalanceSheetBookmark(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [canPersist, authToken, resolvedUserId, onUnauthorized, selectedStock]);

  const getChartCanvas = (): HTMLCanvasElement | null => {
    const root = stockPriceChartSectionRef.current;
    if (!root) return null;
    const canvas = root.querySelector('canvas');
    return (canvas as HTMLCanvasElement | null) ?? null;
  };

  const captureStockPriceSection = async (): Promise<HTMLCanvasElement | null> => {
    const el = stockPriceChartSectionRef.current;
    if (!el) return null;

    const bg = window.getComputedStyle(el).backgroundColor || '#ffffff';
    const width = Math.max(1, (el as HTMLElement).scrollWidth || el.clientWidth || 1);
    const height = Math.max(1, (el as HTMLElement).scrollHeight || el.clientHeight || 1);
    const maxCanvasDim = 12000;
    const scaleCap = maxCanvasDim / Math.max(width, height);
    const scale = Math.max(1, Math.min(2, scaleCap || 1));

    try {
      return await html2canvas(
        el,
        {
          backgroundColor: bg,
          scale,
          useCORS: true,
          logging: false,
        } as any
      );
    } catch {
      return null;
    }
  };

  const captureBalanceSheetLiquiditySection = async (): Promise<HTMLCanvasElement | null> => {
    const el = balanceSheetLiquiditySectionRef.current;
    if (!el) return null;

    const bg = window.getComputedStyle(el).backgroundColor;
    const backgroundColor = !bg || bg === 'transparent' || bg === 'rgba(0, 0, 0, 0)' ? '#ffffff' : bg;
    const width = Math.max(1, (el as HTMLElement).scrollWidth || el.clientWidth || 1);
    const height = Math.max(1, (el as HTMLElement).scrollHeight || el.clientHeight || 1);
    const maxCanvasDim = 12000;
    const scaleCap = maxCanvasDim / Math.max(width, height);
    const scale = Math.max(1, Math.min(2, scaleCap || 1));

    try {
      return await html2canvas(
        el,
        {
          backgroundColor,
          scale,
          useCORS: true,
          logging: false,
        } as any
      );
    } catch {
      return null;
    }
  };

  const toDataUrlWithFallback = (canvas: HTMLCanvasElement, type: string, quality: number) => {
    try {
      const dataUrl = canvas.toDataURL(type, quality);
      if (typeof dataUrl === 'string' && dataUrl.startsWith('data:')) return { type, dataUrl };
      // ignore and fall back
    } catch {
      // ignore and fall back
    }

    const fallbackType = 'image/png';
    return { type: fallbackType, dataUrl: canvas.toDataURL(fallbackType) };
  };

  const makeThumbDataUrl = async (canvas: HTMLCanvasElement, maxWidthPx: number, quality: number): Promise<string> => {
    const srcW = canvas.width;
    const srcH = canvas.height;
    if (!srcW || !srcH) return toDataUrlWithFallback(canvas, 'image/webp', quality).dataUrl;

    const scale = Math.min(1, maxWidthPx / srcW);
    const dstW = Math.max(1, Math.floor(srcW * scale));
    const dstH = Math.max(1, Math.floor(srcH * scale));

    const thumb = document.createElement('canvas');
    thumb.width = dstW;
    thumb.height = dstH;
    const ctx = thumb.getContext('2d');
    if (!ctx) return toDataUrlWithFallback(canvas, 'image/webp', quality).dataUrl;

    ctx.drawImage(canvas, 0, 0, dstW, dstH);
    return toDataUrlWithFallback(thumb, 'image/webp', quality).dataUrl;
  };

  const saveSummaryBookmark = async () => {
    if (!canPersist) {
      const msg = 'Please login to save screenshots.';
      onNotify?.(msg, 'error');
      return;
    }
    if (savingBookmark) return;

    // While the icon is in the 30s "saved" state, ignore repeat clicks.
    // After it returns to default, allow saving again.
    if (bookmarkFlashById['stock-price']) return;

    setSavingBookmark(true);
    try {
      const title = `Stock Price (${selectedTimeRange})`;
      const captured = await captureStockPriceSection();
      if (!captured) {
        // Fallback: at least try capturing the chart canvas only.
        const fallbackCanvas = getChartCanvas();
        if (!fallbackCanvas) {
          const msg = 'Chart is not ready to capture yet.';
          onNotify?.(msg, 'error');
          return;
        }

        const fullFallbackHi = toDataUrlWithFallback(fallbackCanvas, 'image/webp', 0.92);
        // Thumbnails are shown in fairly large tiles (e.g. 535px wide). Use a higher-res thumb
        // so the grid view is crisp without needing expand.
        const thumbFallback = await makeThumbDataUrl(fallbackCanvas, 1100, 0.82);

        await adminApi.createResearchDeskScreenshot(authToken as string, {
          userId: resolvedUserId,
          sourcePage: 'Summary',
          stockSymbol: selectedStock,
          title,
          imageDataUrl: fullFallbackHi.dataUrl,
          thumbDataUrl: thumbFallback,
        });

        setDidBookmark(true);
        triggerBookmarkFlash('stock-price');
        const msg = `${selectedStock} • ${title} • Screenshot saved`;
        void msg;
        return;
      }

      const full = toDataUrlWithFallback(captured, 'image/webp', 0.92);
      const thumbDataUrl = await makeThumbDataUrl(captured, 1100, 0.82);

      await adminApi.createResearchDeskScreenshot(authToken as string, {
        userId: resolvedUserId,
        sourcePage: 'Summary',
        stockSymbol: selectedStock,
        title,
        imageDataUrl: full.dataUrl,
        thumbDataUrl,
      });

      setDidBookmark(true);
      triggerBookmarkFlash('stock-price');
      const msg = `${selectedStock} • ${title} • Screenshot saved`;
      void msg;
    } catch (e: any) {
      if (isUnauthorized(e)) {
        onUnauthorized?.();
        return;
      }
      const msg = e?.message || 'Failed saving screenshot';
      onNotify?.(msg, 'error');
    } finally {
      setSavingBookmark(false);
    }
  };

  const saveBalanceSheetBookmark = async () => {
    if (!canPersist) {
      const msg = 'Please login to save screenshots.';
      onNotify?.(msg, 'error');
      return;
    }
    if (savingBalanceSheetBookmark) return;

    // While the icon is in the 30s "saved" state, ignore repeat clicks.
    // After it returns to default, allow saving again.
    if (bookmarkFlashById['balance-sheet']) return;

    setSavingBalanceSheetBookmark(true);
    try {
      const title = 'Balance Sheet & Liquidity';
      const captured = await captureBalanceSheetLiquiditySection();
      if (!captured) {
        const msg = 'Section is not ready to capture yet.';
        onNotify?.(msg, 'error');
        return;
      }

      const full = toDataUrlWithFallback(captured, 'image/webp', 0.92);
      const thumbDataUrl = await makeThumbDataUrl(captured, 1100, 0.82);

      await adminApi.createResearchDeskScreenshot(authToken as string, {
        userId: resolvedUserId,
        sourcePage: 'Summary',
        stockSymbol: selectedStock,
        title,
        imageDataUrl: full.dataUrl,
        thumbDataUrl,
      });

      setDidBalanceSheetBookmark(true);
      triggerBookmarkFlash('balance-sheet');
      const msg = `${selectedStock} • ${title} • Screenshot saved`;
      void msg;
    } catch (e: any) {
      if (isUnauthorized(e)) {
        onUnauthorized?.();
        return;
      }
      const msg = e?.message || 'Failed saving screenshot';
      onNotify?.(msg, 'error');
    } finally {
      setSavingBalanceSheetBookmark(false);
    }
  };

  // Function to filter data based on time range
  const filterDataByTimeRange = (data: any[], range: string) => {
    const now = new Date();
    let startDate = new Date();
    const sortedByDate = [...data].sort(
      (a: any, b: any) => new Date(a?.date).getTime() - new Date(b?.date).getTime()
    );

    switch (range) {
      case '5D':
        // Use the last 5 trading data points (not calendar days).
        return sortedByDate.slice(-5);
      case '1M':
        startDate.setMonth(now.getMonth() - 1);
        break;
      case '3M':
        startDate.setMonth(now.getMonth() - 3);
        break;
      case 'YTD':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
      case '1Y':
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      case '3Y':
        startDate.setFullYear(now.getFullYear() - 3);
        break;
      case '5Y':
        startDate.setFullYear(now.getFullYear() - 5);
        break;
      case '10Y':
        startDate.setFullYear(now.getFullYear() - 10);
        break;
      case 'All':
        return data;
      default:
        return data;
    }

    startDate.setHours(0, 0, 0, 0);

    return data.filter(item => new Date(item.date) >= startDate);
  };

  const rangeReturnInfo = useMemo((): {
    label: string;
    isPositive: boolean;
    aboveLowLabel: string;
    belowHighLabel: string;
  } | null => {
    if (allData.length === 0) return null;
    // Always sort by date first (oldest -> newest) so "current" is the latest day
    // and the range window is deterministic even if the upstream API order changes.
    const filtered = [...filterDataByTimeRange(allData, selectedTimeRange)].sort(
      (a: any, b: any) => new Date(a?.date).getTime() - new Date(b?.date).getTime()
    );
    if (filtered.length < 2) return null;

    const firstClose = Number(filtered[0]?.close);
    const lastClose = Number(filtered[filtered.length - 1]?.close);
    if (!Number.isFinite(firstClose) || !Number.isFinite(lastClose) || firstClose === 0) return null;

    // Prefer real-time quote price when available (GuruFocus-style behavior).
    const currentPriceCandidate = Number(quoteData?.price);
    const currentPrice = (Number.isFinite(currentPriceCandidate) && currentPriceCandidate > 0)
      ? currentPriceCandidate
      : lastClose;

    const closes = filtered
      .map(p => Number(p?.close))
      .filter(v => Number.isFinite(v) && v > 0);
    if (closes.length === 0) return null;

    // Match Below High behavior: reference points come from CLOSE, not intraday HIGH/LOW.
    // Above Low uses the lowest close in the selected period.
    const baseMinClose = Math.min(...closes);
    const baseMaxClose = Math.max(...closes);
    if (!Number.isFinite(baseMinClose) || !Number.isFinite(baseMaxClose) || baseMinClose <= 0 || baseMaxClose <= 0) return null;

    const minLow = baseMinClose;

    // User requirement: for historical-price-eod/full, compute the "high" reference using CLOSE (not the day's HIGH).
    // This means "Below High" is relative to the highest close in the selected period.
    const maxHigh = baseMaxClose;

    if (!Number.isFinite(minLow) || !Number.isFinite(maxHigh) || minLow <= 0 || maxHigh <= 0) return null;

    const pct = ((currentPrice / firstClose) - 1) * 100;
    if (!Number.isFinite(pct)) return null;

    const labelKey = selectedTimeRange;

    const formatSignedPct2 = (pctValue: number) => {
      // Avoid showing "-0.00%" due to floating point noise.
      const v = Math.abs(pctValue) < 1e-9 ? 0 : pctValue;
      if (v === 0) return '0.00%';
      return `${v > 0 ? '+' : ''}${v.toFixed(2)}%`;
    };

    // Above Low definition: (Current Price - Low) / Low
    // This is >= 0 when current >= low, and equals 0 only when current === low.
    const aboveLowPct = ((currentPrice - minLow) / minLow) * 100;
    // Below High definition: (Current Price - High) / High
    // This is <= 0 when current <= high, and equals 0 only when current === high.
    const belowHighPct = ((currentPrice - maxHigh) / maxHigh) * 100;
    if (!Number.isFinite(aboveLowPct) || !Number.isFinite(belowHighPct)) return null;

    const aboveLowSigned = formatSignedPct2(aboveLowPct);
    const belowHighSigned = formatSignedPct2(belowHighPct);

    const pctSigned2 = formatSignedPct2(pct);

    return {
      label: `${labelKey}: ${pctSigned2}`,
      isPositive: pct >= 0,
      aboveLowLabel: `Above Low: ${aboveLowSigned}`,
      belowHighLabel: `Below High: ${belowHighSigned}`,
    };
  }, [allData, selectedTimeRange, quoteData]);

  // Update chart when time range changes
  useEffect(() => {
    if (allData.length > 0) {
      const filteredData = [...filterDataByTimeRange(allData, selectedTimeRange)].sort(
        (a: any, b: any) => new Date(a?.date).getTime() - new Date(b?.date).getTime()
      );

      if (filteredData.length > 0) {
        const todayKey = new Date().toISOString().slice(0, 10);
        const isAllRange = selectedTimeRange === 'All';
        setChartBounds({
          min: filteredData[0].date,
          // For All range: show up to "today" (current year label), but never beyond.
          max: isAllRange ? todayKey : filteredData[filteredData.length - 1].date,
        });
      } else {
        setChartBounds(null);
      }
      
      setChartData({
        labels: filteredData.map(item => item.date),
        datasets: [{
          label: 'Price',
          data: filteredData.map(item => ({ x: item.date, y: item.close })),
          borderColor: '#2E55E9',
          borderWidth: 2,
          pointRadius: 0,
          tension: 0.4,
          fill: true,
        }]
      });
    }
  }, [allData, selectedTimeRange]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const metricsCatalog = await fetchFiscalStandardizedMetricsList();
        // Fetch all data in parallel (service layer handles API key gating / fallbacks)
        const [
          prices,
          balance,
          cashFlow,
          income,
          incomeTtmSeries,
          keyMetricsQuarter,
          keyMetricsTtm,
          fiscalIncomeTtm,
          fiscalCashTtm,
          fiscalBalanceTtm,
          fiscalRoicRatios,
          fiscalMarketCapRatios,
          fiscalDescription,
        ] = await Promise.all([
          fetchHistoricalPrices(selectedStock),
          fetchBalanceSheetTTM(selectedStock),
          fetchCashFlowStatementTTM(selectedStock),
          fetchIncomeStatementTTM(selectedStock),
          fetchIncomeStatementTTMSeries(selectedStock, 120),
          fetchKeyMetrics(selectedStock, 'quarter', 40),
          fetchKeyMetricsTTM(selectedStock),
          fetchFiscalStandardizedIncomeStatementTtm(selectedStock, metricsCatalog),
          fetchFiscalStandardizedCashFlowStatementTtm(selectedStock, metricsCatalog),
          fetchFiscalStandardizedBalanceSheetTtm(selectedStock, metricsCatalog),
          fetchFiscalRoicRatios(selectedStock),
          fetchFiscalMarketCapRatios(selectedStock),
          fetchFiscalCompanyDescription(selectedStock),
        ]);

        if (Array.isArray(prices) && prices.length > 0) {
          setAllData(prices);
        } else {
          setAllData([]);
        }

        const fiscalErrorText = String(fiscalIncomeTtm.error || fiscalCashTtm.error || fiscalBalanceTtm.error || '').trim();
        const fiscalDataCount = (fiscalIncomeTtm.row ? 1 : 0) + (fiscalCashTtm.row ? 1 : 0) + (fiscalBalanceTtm.row ? 1 : 0);
        const unavailableByMessage = Boolean(fiscalErrorText) && /data is not yet available|companies-list|companies list/i.test(fiscalErrorText);
        const fiscalAvailable = !unavailableByMessage && fiscalDataCount > 0;
        setDataSource(fiscalAvailable ? 'fiscal' : 'fmp');
        setCompanyDescription(fiscalDescription ?? '');

        // Quote is loaded separately via backend quotes-cache when logged in.
        
        // Calculate metrics (TTM statements)
        const marketCapFromKeyMetrics = (() => {
                  const ttm = (keyMetricsTtm as any)?.marketCap;
                  if (typeof ttm === 'number' && Number.isFinite(ttm) && ttm > 0) return ttm;

                  const kmList = Array.isArray(keyMetricsQuarter) ? keyMetricsQuarter : [];
                  const latest = kmList
                    .filter((km: any) => km && km.date)
                    .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
                  const q = latest?.marketCap;
                  if (typeof q === 'number' && Number.isFinite(q) && q > 0) return q;
                  return null;
                })();

        const toFiniteNumber = (value: unknown): number | null => {
          if (value == null) return null;
          const num = typeof value === 'number' ? value : Number(value);
          return Number.isFinite(num) ? num : null;
        };

        const pickNumber = (primary: unknown, fallback: unknown): number | null => {
          const primaryNum = toFiniteNumber(primary);
          if (primaryNum != null) return primaryNum;
          return toFiniteNumber(fallback);
        };

        const fiscalBalance = fiscalBalanceTtm.row;
        const fiscalCash = fiscalCashTtm.row;
        const fiscalIncome = fiscalIncomeTtm.row;

        const fiscalMarketCapLatest = (() => {
          if (!fiscalAvailable) return null;
          const rows = Array.isArray(fiscalMarketCapRatios) ? fiscalMarketCapRatios : [];
          const normalizedRows = rows
            .map((row) => ({
              parsedValue: toFiniteNumber(row?.value),
              ts: row?.date ? new Date(row.date).getTime() : Number.NaN,
              periodType: String(row?.periodType || '').toLowerCase(),
            }))
            .filter((row) => row.parsedValue != null && (row.parsedValue as number) > 0 && Number.isFinite(row.ts));

          if (!normalizedRows.length) return null;

          const ltmRows = normalizedRows
            .filter((row) => row.periodType === 'ltm')
            .sort((a, b) => a.ts - b.ts);
          if (ltmRows.length) return ltmRows[ltmRows.length - 1].parsedValue;

          normalizedRows.sort((a, b) => a.ts - b.ts);
          return normalizedRows[normalizedRows.length - 1].parsedValue;
        })();

        const fiscalCashAndShortTerm = fiscalAvailable
          ? (fiscalBalance?.cashAndShortTermInvestments ?? fiscalBalance?.cashAndCashEquivalents ?? fiscalBalance?.cashAndCashEquivalents ?? null)
          : null;
        const cashAndShortTermInvestments = pickNumber(
          fiscalCashAndShortTerm,
          balance?.cashAndShortTermInvestments ?? balance?.cashAndCashEquivalents ?? balance?.cashAndCashEquivalents ?? null
        );

        const longTermDebt = pickNumber(
          fiscalAvailable ? fiscalBalance?.longTermDebt : null,
          balance?.longTermDebt ?? null
        );

        const fiscalOperatingCashFlow = fiscalAvailable
          ? toFiniteNumber(fiscalCash?.operatingCashFlow ?? fiscalCash?.netCashProvidedByOperatingActivities ?? null)
          : null;
        const fiscalCapex = fiscalAvailable
          ? toFiniteNumber(fiscalCash?.capitalExpenditure ?? fiscalCash?.investmentsInPropertyPlantAndEquipment ?? null)
          : null;
        const fiscalFreeCashFlow = (fiscalOperatingCashFlow != null && fiscalCapex != null)
          ? fiscalOperatingCashFlow + fiscalCapex
          : null;

        const freeCashFlow = pickNumber(
          fiscalFreeCashFlow,
          cashFlow?.freeCashFlow ?? null
        );
        const operatingCashFlow = pickNumber(
          fiscalOperatingCashFlow,
          cashFlow?.operatingCashFlow ?? cashFlow?.netCashProvidedByOperatingActivities ?? null
        );

        const revenue = pickNumber(
          fiscalAvailable ? fiscalIncome?.revenue : null,
          income?.revenue ?? null
        );
        const grossProfit = pickNumber(
          fiscalAvailable ? fiscalIncome?.grossProfit : null,
          income?.grossProfit ?? null
        );
        const operatingIncome = pickNumber(
          fiscalAvailable ? fiscalIncome?.operatingIncome : null,
          income?.operatingIncome ?? null
        );
        const netIncome = pickNumber(
          fiscalAvailable ? fiscalIncome?.netIncome : null,
          income?.netIncome ?? null
        );

        // ROIC (TTM): keep aligned with Growth + Key Metrics screens.
        // Compute TTM from quarterly key-metrics using invested-capital weighting.
        const roicTtmFromQuarterly = (() => {
          const kmList = Array.isArray(keyMetricsQuarter) ? keyMetricsQuarter : [];
          const kmAsc = [...kmList]
            .filter((km: any) => km && km.date)
            .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());

          if (kmAsc.length < 4) return null;

          const getRoicFraction = (km: any): number | null => {
            const raw = (km as any)?.returnOnInvestedCapital ?? (km as any)?.roic;
            const num = typeof raw === 'number' ? raw : Number(raw);
            return Number.isFinite(num) ? num : null;
          };

          let latest: number | null = null;

          for (let i = 3; i < kmAsc.length; i++) {
            let nopatSum = 0;
            let investedCapSum = 0;
            let investedCapCount = 0;

            for (let j = i - 3; j <= i; j++) {
              const km = kmAsc[j];
              const icRaw = (km as any)?.investedCapital;
              const investedCapital = typeof icRaw === 'number' ? icRaw : Number(icRaw);
              const roicQ = getRoicFraction(km);
              if (!Number.isFinite(investedCapital) || investedCapital <= 0) continue;
              if (roicQ === null) continue;
              nopatSum += roicQ * investedCapital;
              investedCapSum += investedCapital;
              investedCapCount++;
            }

            const investedCapAvg = investedCapCount ? investedCapSum / investedCapCount : 0;
            const roicTtm = investedCapAvg > 0 ? nopatSum / investedCapAvg : NaN;
            if (!Number.isFinite(roicTtm)) continue;
            latest = roicTtm;
          }

          return latest;
        })();

        // Fallback: some accounts/symbols may not have enough quarterly history.
        const roicFromKeyMetricsTtm = (keyMetricsTtm as any)?.returnOnInvestedCapitalTTM ?? null;
        const roicFallback = (typeof roicFromKeyMetricsTtm === 'number' && Number.isFinite(roicFromKeyMetricsTtm))
          ? roicFromKeyMetricsTtm
          : null;

        const fiscalRoicLatest = (() => {
          if (!fiscalAvailable) return null;
          const rows = Array.isArray(fiscalRoicRatios) ? fiscalRoicRatios : [];
          const candidates = rows
            .filter((row) => row && row.date && toFiniteNumber(row.value) != null)
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
          if (!candidates.length) return null;
          return toFiniteNumber(candidates[candidates.length - 1]?.value);
        })();

        const roic = (fiscalRoicLatest != null)
          ? fiscalRoicLatest
          : (typeof roicTtmFromQuarterly === 'number' && Number.isFinite(roicTtmFromQuarterly))
            ? roicTtmFromQuarterly
            : roicFallback;
        
        // P/E (TTM): keep consistent with the Charts page series, which is price / EPS (TTM).
        // Prefer the last available close price (from historical prices) and diluted EPS (TTM).
        // Fall back to API key-metrics TTM peRatio, then to marketCap/netIncome when needed.
        const lastPriceRow = (Array.isArray(prices) && prices.length > 0)
          ? (prices[prices.length - 1] as any)
          : null;
        const lastClosePrice = lastPriceRow?.close ?? null;
        const lastPriceDate = lastPriceRow?.date ? new Date(lastPriceRow.date) : null;

        // Charts P/E is computed as: price(close) / EPS(TTM), where EPS(TTM) is taken from
        // the income-statement-ttm series (latest row at-or-before the price date).
        const ttmEpsPoints = (Array.isArray(incomeTtmSeries) ? incomeTtmSeries : [])
          .map((row: any) => ({ date: new Date(row.date), eps: row.epsDiluted }))
          .filter((p: any) => p.date instanceof Date && !Number.isNaN(p.date.getTime()) && typeof p.eps === 'number' && Number.isFinite(p.eps) && p.eps > 0)
          .sort((a: any, b: any) => a.date.getTime() - b.date.getTime());

        const latestEpsForLastPrice = (lastPriceDate && ttmEpsPoints.length > 0)
          ? ttmEpsPoints.filter(p => p.date <= lastPriceDate).slice(-1)[0]?.eps
          : null;

        const peFromPriceAndEps = (typeof lastClosePrice === 'number' && Number.isFinite(lastClosePrice) &&
          typeof latestEpsForLastPrice === 'number' && Number.isFinite(latestEpsForLastPrice) && latestEpsForLastPrice !== 0)
          ? lastClosePrice / latestEpsForLastPrice
          : null;

        const peFromKeyMetrics = (keyMetricsTtm && typeof (keyMetricsTtm as any).peRatio === 'number' && Number.isFinite((keyMetricsTtm as any).peRatio))
          ? (keyMetricsTtm as any).peRatio
          : null;

        const peFromMarketCap = (marketCapFromKeyMetrics && netIncome && netIncome !== 0)
          ? marketCapFromKeyMetrics / netIncome
          : null;

        const peRatioDefault = peFromPriceAndEps ?? peFromKeyMetrics ?? peFromMarketCap;

        const reportedCurrency = String(
          income?.reportedCurrency ?? balance?.reportedCurrency ?? cashFlow?.reportedCurrency ?? ''
        ).trim().toUpperCase();
        const shouldUseFiscalOnlyValuation = fiscalAvailable && reportedCurrency.length > 0 && reportedCurrency !== 'USD';

        const fiscalNetIncome = toFiniteNumber(fiscalIncome?.netIncome ?? null);
        const fiscalEpsDiluted = toFiniteNumber(fiscalIncome?.epsDiluted ?? fiscalIncome?.eps ?? null);
        const fiscalDerivedShares = (
          fiscalNetIncome != null && fiscalEpsDiluted != null && fiscalEpsDiluted !== 0
        )
          ? (fiscalNetIncome / fiscalEpsDiluted)
          : null;
        const fiscalOcfPerShare = (
          fiscalOperatingCashFlow != null && fiscalDerivedShares != null && fiscalDerivedShares > 0
        )
          ? (fiscalOperatingCashFlow / fiscalDerivedShares)
          : null;
        const fiscalFcfPerShare = (
          fiscalFreeCashFlow != null && fiscalDerivedShares != null && fiscalDerivedShares > 0
        )
          ? (fiscalFreeCashFlow / fiscalDerivedShares)
          : null;

        const fiscalPeFromPrice = (
          typeof lastClosePrice === 'number' && Number.isFinite(lastClosePrice) && lastClosePrice > 0 &&
          fiscalEpsDiluted != null && Number.isFinite(fiscalEpsDiluted) && fiscalEpsDiluted > 0
        )
          ? (lastClosePrice / fiscalEpsDiluted)
          : null;
        const fiscalPocfFromPrice = (
          typeof lastClosePrice === 'number' && Number.isFinite(lastClosePrice) && lastClosePrice > 0 &&
          fiscalOcfPerShare != null && Number.isFinite(fiscalOcfPerShare) && fiscalOcfPerShare > 0
        )
          ? (lastClosePrice / fiscalOcfPerShare)
          : null;
        const fiscalPfcfFromPrice = (
          typeof lastClosePrice === 'number' && Number.isFinite(lastClosePrice) && lastClosePrice > 0 &&
          fiscalFcfPerShare != null && Number.isFinite(fiscalFcfPerShare) && fiscalFcfPerShare > 0
        )
          ? (lastClosePrice / fiscalFcfPerShare)
          : null;

        const marketCap = shouldUseFiscalOnlyValuation
          ? toFiniteNumber(fiscalMarketCapLatest)
          : marketCapFromKeyMetrics;
        const peRatio = shouldUseFiscalOnlyValuation
          ? (
              fiscalPeFromPrice ??
              ((fiscalMarketCapLatest && fiscalNetIncome && fiscalNetIncome !== 0)
                ? fiscalMarketCapLatest / fiscalNetIncome
                : null)
            )
          : peRatioDefault;
        const priceToOCF = shouldUseFiscalOnlyValuation
          ? (
              fiscalPocfFromPrice ??
              ((fiscalMarketCapLatest && fiscalOperatingCashFlow && fiscalOperatingCashFlow !== 0)
                ? fiscalMarketCapLatest / fiscalOperatingCashFlow
                : null)
            )
          : ((marketCapFromKeyMetrics && operatingCashFlow && operatingCashFlow !== 0)
              ? marketCapFromKeyMetrics / operatingCashFlow
              : null);
        const priceToFCF = shouldUseFiscalOnlyValuation
          ? (
              fiscalPfcfFromPrice ??
              ((fiscalMarketCapLatest && fiscalFreeCashFlow && fiscalFreeCashFlow !== 0)
                ? fiscalMarketCapLatest / fiscalFreeCashFlow
                : null)
            )
          : ((marketCapFromKeyMetrics && freeCashFlow && freeCashFlow !== 0)
              ? marketCapFromKeyMetrics / freeCashFlow
              : null);
        
        setMetrics({
          cash: cashAndShortTermInvestments,
          longTermDebt,
          cashToDebtRatio:
            (cashAndShortTermInvestments && longTermDebt && longTermDebt !== 0)
              ? cashAndShortTermInvestments / longTermDebt
              : null,
          fcfToDebtRatio: (freeCashFlow && longTermDebt && longTermDebt !== 0) ? freeCashFlow / longTermDebt : null,
          grossMargin: (grossProfit && revenue && revenue !== 0) ? grossProfit / revenue : null,
          operatingMargin: (operatingIncome && revenue && revenue !== 0) ? operatingIncome / revenue : null,
          netMargin: (netIncome && revenue && revenue !== 0) ? netIncome / revenue : null,
          fcfMargin: (freeCashFlow && revenue && revenue !== 0) ? freeCashFlow / revenue : null,
          marketCap,
          peRatio,
          priceToOCF,
          priceToFCF,
          volume: (() => {
            const volumes = (Array.isArray(prices) ? prices : [])
              .map((p: any) => p?.volume)
              .filter((v: any) => typeof v === 'number' && Number.isFinite(v) && v > 0);

            // Prefer 20-day average volume from EOD history (not realtime).
            if (volumes.length > 0) {
              const last20 = volumes.slice(-20);
              const avg = last20.reduce((sum: number, v: number) => sum + v, 0) / last20.length;
              return Number.isFinite(avg) ? avg : null;
            }
            return null;
          })(),
          roic,
        });
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };
    
    fetchData();
  }, [selectedStock]);

  const formatNumber = (num: number | null, suffix: string = '') => {
    if (num === null) return '-';
    if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T${suffix}`;
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B${suffix}`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M${suffix}`;
    if (num >= 1e3) return `$${(num / 1e3).toFixed(2)}K${suffix}`;
    return `$${num.toFixed(2)}${suffix}`;
  };

  const formatPercent = (num: number | null) => {
    if (num === null) return '-';
    return `${(num * 100).toFixed(2)}%`;
  };

  const formatRoicPercent = (num: number | null) => {
    if (num === null) return '-';
    if (!Number.isFinite(num)) return '-';

    // Match Growth + Key Metrics presentation: one decimal, rounded.
    // API can return ROIC either as a ratio (0.2387) or already as percent (23.87).
    const valueInPercent = Math.abs(num) < 1 ? num * 100 : num;
    return `${valueInPercent.toFixed(1)}%`;
  };

  const formatRatio = (num: number | null) => {
    if (num === null) return '-';
    return num.toFixed(2);
  };

  const formatRatioNonNegative = (num: number | null) => {
    if (num === null) return 'N/A';
    if (!Number.isFinite(num)) return 'N/A';
    if (num < 0) return 'N/A';
    return num.toFixed(2);
  };

  const formatPercentNonNegative = (num: number | null) => {
    if (num === null) return 'N/A';
    if (!Number.isFinite(num)) return 'N/A';
    if (num < 0) return 'N/A';
    return `${(num * 100).toFixed(1)}%`;
  };

  const formatPeRatio = (num: number | null) => {
    if (num === null) return '-';
    if (!Number.isFinite(num)) return 'N/A';
    if (num < 0) return 'N/A';
    // Keep display identical to Charts (floors to 2 decimals).
    return (Math.floor(num * 100) / 100).toFixed(2);
  };

  const containerStyle: React.CSSProperties = {
    // Keep equal spacing between Research header bars and the Summary screen.
    // - stock-info-bar -> tabs-bar is 15px (in Header)
    // - tabs-bar -> research-summary-screen is also 15px (here)
    marginTop: '17px',
    // Adapt horizontal padding for smaller screens without changing the 15px vertical gaps.
    padding: '0 clamp(1rem, 4vw, 3rem) 2rem clamp(1rem, 4vw, 3rem)',
    paddingTop: '0px',
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    minHeight: 'calc(100vh - 15rem)',
    color: isDarkMode ? '#e5e7eb' : '#1f2937',
  };

  const descriptionSectionStyle: React.CSSProperties = {
    marginBottom: '2rem',
    padding: '1.5rem',
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    borderRadius: '0.5rem',
    border: `1px solid ${isDarkMode ? '#374151' : '#D9D9D9'}`,
    width: '1760px',
    marginLeft: '0px',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: 'Manrope, sans-serif',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '16px',
    lineHeight: '1',
    letterSpacing: '0%',
    color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
  };

  const showSourceLabels = Boolean(isAdmin);
  const metricsSourceLabelText = dataSource === 'fiscal' ? 'fiscal.ai' : 'FMP';
  const normalizedCompanyDescription = String(companyDescription ?? '').trim();
  const hasCompanyDescription = normalizedCompanyDescription.length > 0;
  const descriptionText = normalizedCompanyDescription;
  const descriptionSourceLabel = hasCompanyDescription ? 'fiscal.ai' : null;
  const sourceLabelStyle: React.CSSProperties = {
    marginLeft: '0.5rem',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: isDarkMode ? '#9CA3AF' : '#6B7280',
  };
  const floatingSourceLabelStyle: React.CSSProperties = {
    position: 'absolute',
    right: '0.75rem',
    top: '0.65rem',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: isDarkMode ? '#9CA3AF' : '#6B7280',
    pointerEvents: 'none',
    zIndex: 2,
    padding: '0.1rem 0.35rem',
    background: isDarkMode ? '#1f2937' : '#ffffff',
    borderRadius: '6px',
    border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
  };
  const metricsSourceLabelStyle: React.CSSProperties = {
    ...floatingSourceLabelStyle,
    top: '-0.55rem',
    background: isDarkMode ? '#111827' : '#ffffff',
  };
  const descriptionHtmlStyle: React.CSSProperties = {
    lineHeight: '1.6',
    fontSize: '16px',
    color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
  };

  const escapeHtml = (value: string) =>
    value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const descriptionHtml = useMemo(() => {
    const raw = String(descriptionText ?? '').trim();
    if (!raw) return '';
    if (/<[a-z][\s\S]*>/i.test(raw)) return raw;
    const escaped = escapeHtml(raw);
    return `<p>${escaped.replace(/\n{2,}/g, '</p><p>').replace(/\n/g, '<br />')}</p>`;
  }, [descriptionText]);

  const topSectionStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '900px 840px',
    gap: '19px',
    marginBottom: '30px',
  };

  const chartContainerStyle: React.CSSProperties = {
    padding: '1.5rem',
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    borderRadius: '0.5rem',
    border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    height: '397px',
    boxSizing: 'border-box',
  };

  const chartHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  };

  const timeRangeStyle: React.CSSProperties = {
    display: 'flex',
    gap: '0.5rem',
  };

  const timeButtonStyle = (active: boolean): React.CSSProperties => ({
    padding: '0.375rem 0.875rem',
    backgroundColor: active ? '#1f2937' : 'transparent',
    color: isDarkMode ? '#F9FAFB' : (active ? 'white' : '#6b7280'),
    border: 'none',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: active ? '600' : '500',
    transition: 'all 0.2s ease',
  });

  const metricsGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1.5rem',
  };

  const metricCardStyle: React.CSSProperties = {
    padding: '1rem',
    backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
    border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
    borderRadius: '0.375rem',
    height: '397px',
    boxSizing: 'border-box',
    color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
    position: 'relative',
  };

  const metricRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.4rem 0',
  };

  const xTimeUnit =
    selectedTimeRange === '5D' ? 'day'
    : selectedTimeRange === '1M' ? 'day'
    : selectedTimeRange === '3M' ? 'week'
    : selectedTimeRange === 'YTD' ? 'month'
    : selectedTimeRange === '1Y' ? 'month'
    : selectedTimeRange === '3Y' ? 'quarter'
    : selectedTimeRange === '5Y' ? 'year'
    : selectedTimeRange === '10Y' ? 'year'
    : 'year';

  const isAllRange = selectedTimeRange === 'All';
  const isFiveDayRange = selectedTimeRange === '5D';

  const renderStockPriceTooltip = (context: any) => {
    const { chart, tooltip } = context;
    const parent = (chart?.canvas?.parentNode as HTMLElement | null) ?? null;
    if (!parent) return;

    if (window.getComputedStyle(parent).position === 'static') {
      parent.style.position = 'relative';
    }

    let tooltipEl = parent.querySelector<HTMLDivElement>('.summary-stock-price-tooltip');
    if (!tooltipEl) {
      tooltipEl = document.createElement('div');
      tooltipEl.className = 'summary-stock-price-tooltip';
      tooltipEl.style.position = 'absolute';
      tooltipEl.style.pointerEvents = 'none';
      tooltipEl.style.width = '109px';
      tooltipEl.style.height = '68px';
      tooltipEl.style.padding = '12px';
      tooltipEl.style.borderRadius = '8px';
      tooltipEl.style.backgroundColor = '#0D0D0D';
      tooltipEl.style.display = 'flex';
      tooltipEl.style.flexDirection = 'column';
      tooltipEl.style.gap = '6px';
      tooltipEl.style.boxSizing = 'border-box';
      tooltipEl.style.opacity = '0';
      tooltipEl.style.transform = 'translate(10px, -50%)';
      parent.appendChild(tooltipEl);
    }

    if (!tooltip || tooltip.opacity === 0) {
      tooltipEl.style.opacity = '0';
      return;
    }

    const point = tooltip.dataPoints?.[0];
    const year = point ? new Date(point.parsed.x).getFullYear().toString() : '';
    const value = point
      ? `$${Number(point.parsed.y).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : '';

    tooltipEl.innerHTML = `
      <div style=";border-radius:6px;padding:4px 6px;text-align:left;font-family:Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;font-weight:700;font-size:12px;line-height:100%;letter-spacing:0%;color:#D1D5DB;">
        ${year}
      </div>
      <div style="border-radius:6px;padding:4px 6px;text-align:left;font-family:Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;font-weight:700;font-size:16px;line-height:100%;letter-spacing:0%;color:#F9FAFB;">
        ${value}
      </div>
    `;

    const canvasRect = chart.canvas.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();
    const left = canvasRect.left - parentRect.left + tooltip.caretX;
    const top = canvasRect.top - parentRect.top + tooltip.caretY;

    tooltipEl.style.left = `${left}px`;
    tooltipEl.style.top = `${top}px`;
    tooltipEl.style.opacity = '1';
  };

  return (
    <div id="research-summary-screen" style={containerStyle}>
      {loading ? (
        <CenteredLoadingSpinner isDarkMode={isDarkMode} minHeight="calc(100vh - 15rem)" />
      ) : (
        <>
          {/* Top Section: Chart + Metrics */}
          <div style={topSectionStyle}>
            {/* Left: Stock Price Chart */}
            <div id="stock-price-chart-section" style={chartContainerStyle} ref={stockPriceChartSectionRef}>
              <div style={chartHeaderStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <button 
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      cursor: 'pointer', 
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    title="Expand chart"
                    onClick={() => setIsModalOpen(true)}
                  >
                    <img 
                      src={isDarkMode ? '/images/darkmode/zoomin.svg' : '/images/zoomin.svg'}
                      alt="Expand" 
                      style={{ width: '20px', height: '20px' }}
                    />
                  </button>
                  <span style={{ fontSize: '1rem', fontWeight: '600', color: isDarkMode ? '#F9FAFB' : '#1f2937' }}>Stock Price</span>
                  {showSourceLabels ? <span style={sourceLabelStyle}>FMP</span> : null}
                  {rangeReturnInfo && (
                    <>
                      <span
                        style={{
                          fontSize: '0.875rem',
                          fontWeight: '700',
                          color: isDarkMode ? '#F9FAFB' : (rangeReturnInfo.isPositive ? '#10b981' : '#ef4444'),
                        }}
                      >
                        {rangeReturnInfo.label}
                      </span>
                      <span style={{ fontSize: '0.875rem', fontWeight: '600', color: isDarkMode ? '#F9FAFB' : '#10b981' }}>
                        {rangeReturnInfo.aboveLowLabel}
                      </span>
                      <span style={{ fontSize: '0.875rem', fontWeight: '600', color: isDarkMode ? '#F9FAFB' : '#ef4444' }}>
                        {rangeReturnInfo.belowHighLabel}
                      </span>
                    </>
                  )}
                </div>
                <button 
                  style={{ 
                    background: 'transparent',
                    border: 'none', 
                    borderRadius: '8px',
                    cursor: 'pointer', 
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    outline: 'none',
                    boxShadow: 'none',
                    opacity: savingBookmark ? 0.6 : 1,
                    transition: 'background-color 120ms ease',
                  }}
                  title="Bookmark"
                  disabled={savingBookmark}
                  onClick={saveSummaryBookmark}
                >
                  <img 
                    src={bookmarkFlashById['stock-price'] ? '/images/bookmark_fill.svg' : (isDarkMode ? '/images/darkmode/bookmark_empy.svg' : '/images/bookmark_empy.svg')}
                    alt="Bookmark" 
                    style={{
                      width: '20px',
                      height: '20px',
                    }}
                  />
                </button>
              </div>
              
              <div style={{ ...timeRangeStyle, marginTop: '0.75rem', marginBottom: '0.75rem' }}>
                {['5D', '1M', '3M', 'YTD', '1Y', '3Y', '5Y', '10Y', 'All'].map(range => (
                  <button 
                    key={range} 
                    style={timeButtonStyle(range === selectedTimeRange)}
                    onClick={() => setSelectedTimeRange(range)}
                  >
                    {range}
                  </button>
                ))}
              </div>

              {/* Chart */}
              <div style={{ 
                height: '250px', 
                marginTop: '1rem',
                cursor: 'pointer',
              }}
              role="button"
              tabIndex={0}
              aria-label="Expand chart"
              onClick={() => setIsModalOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsModalOpen(true);
                }
              }}
              >
                {chartData ? (
                  <Line 
                    data={{
                      labels: chartData.labels,
                      datasets: [{
                        label: 'Price',
                        data: chartData.datasets[0].data,
                        borderColor: '#2F58F2',
                        backgroundColor: (context: any) => {
                          const chart = context.chart;
                          const {ctx, chartArea} = chart;
                          if (!chartArea) return 'rgba(47, 88, 242, 0.3)';
                          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                          gradient.addColorStop(0, '#2E55E9');
                          gradient.addColorStop(1, 'rgba(46, 85, 233, 0)');
                          return gradient;
                        },
                        borderWidth: 2,
                        pointRadius: 0,
                        pointHoverRadius: 4,
                        pointHoverBackgroundColor: '#0D0D0D',
                        pointHoverBorderColor: '#0D0D0D',
                        tension: 0.4,
                        fill: true,
                      }]
                    }}
                    plugins={[summaryCrosshairPlugin]}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: ({
                        summaryCrosshair: {
                          enabled: true,
                          color: isDarkMode ? 'rgba(148,163,184,0.55)' : 'rgba(100,116,139,0.55)',
                          dash: [5, 5],
                          width: 1,
                        },
                        legend: {
                          display: false,
                        },
                        tooltip: {
                          mode: 'index',
                          intersect: false,
                          enabled: false,
                          external: renderStockPriceTooltip,
                        },
                      } as any),
                      interaction: {
                        mode: 'index',
                        intersect: false,
                      },
                      scales: {
                        x: {
                          type: 'time' as const,
                          time: {
                            unit: xTimeUnit as any,
                            ...(isAllRange ? { stepSize: 1 } : {}),
                            displayFormats: {
                              day: 'MMM d',
                              week: 'MMM d',
                              month: 'MMM yyyy',
                              quarter: 'MMM yyyy',
                              year: 'yyyy'
                            }
                          },
                          bounds: 'data',
                          grid: {
                            display: false,
                          },
                          ticks: {
                            color: '#6b7280',
                            font: {
                              size: 11,
                            },
                            maxRotation: 0,
                            minRotation: 0,
                            // For All-range, generate clean yearly ticks.
                            // `stepSize` belongs under `time`, not `ticks`.
                            source: isFiveDayRange ? ('data' as const) : ('auto' as const),
                            autoSkip: isAllRange || isFiveDayRange ? false : true,
                            autoSkipPadding: isAllRange || isFiveDayRange ? 0 : 20,
                          },
                          ...(chartBounds ? { min: chartBounds.min, max: chartBounds.max } : {}),
                        },
                        y: {
                          position: 'right' as const,
                          grid: {
                            color: isDarkMode ? '#4B5563' : '#f3f4f6',
                            lineWidth: 1,
                          },
                          ticks: {
                            color: '#6b7280',
                            font: {
                              size: 11,
                            },
                            callback: function(value: any) {
                              return '$' + value.toLocaleString();
                            },
                          },
                        },
                      },
                    }}
                  />
                ) : (
                  <div style={{ height: '100%' }}>
                    <CenteredLoadingSpinner isDarkMode={isDarkMode} minHeight="100%" sizePx={36} />
                  </div>
                )}
              </div>
            </div>

            {/* Right: All Metrics in One Card with 2 columns */}
            <div style={metricCardStyle} ref={balanceSheetLiquiditySectionRef}>
              {showSourceLabels ? (
                <div style={metricsSourceLabelStyle}>{metricsSourceLabelText}</div>
              ) : null}
              <div style={metricsGridStyle}>
                {/* Left Column: Balance Sheet & Liquidity + Profitability Margins */}
                <div>
                  <div id="balance-sheet-liquidity-section">
                    <h3
                      style={{
                        fontFamily: 'Manrope, sans-serif',
                        fontWeight: 600,
                        fontStyle: 'normal',
                        fontSize: '16px',
                        lineHeight: '1',
                        letterSpacing: '0%',
                        color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
                        marginBottom: '0.75rem',
                      }}
                    >
                      Balance Sheet & Liquidity
                    </h3>
                    <div style={metricRowStyle}>
                      <span style={labelStyle}>Cash and Short Term Investments</span>
                      <span style={{ fontWeight: '600' }}>{formatNumber(metrics.cash)}</span>
                    </div>
                    <div style={metricRowStyle}>
                      <span style={labelStyle}>Long Term Debt</span>
                      <span style={{ fontWeight: '600' }}>{formatNumber(metrics.longTermDebt)}</span>
                    </div>
                    <div style={metricRowStyle}>
                      <span style={labelStyle}>Cash to Long Term Debt Ratio</span>
                      <span style={{ fontWeight: '600' }}>{formatRatioNonNegative(metrics.cashToDebtRatio)}</span>
                    </div>
                    <div style={metricRowStyle}>
                      <span style={labelStyle}>Free Cash Flow to Long Term Debt Ratio</span>
                      <span style={{ fontWeight: '600' }}>{formatRatioNonNegative(metrics.fcfToDebtRatio)}</span>
                    </div>
                  </div>

                  <div id="profitability-margins-section" style={{ marginTop: '1.5rem' }}>
                    <h3
                      style={{
                        fontFamily: 'Manrope, sans-serif',
                        fontWeight: 600,
                        fontStyle: 'normal',
                        fontSize: '16px',
                        lineHeight: '1',
                        letterSpacing: '0%',
                        color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
                        marginBottom: '0.75rem',
                      }}
                    >
                      Profitability Margins
                    </h3>
                    <div style={metricRowStyle}>
                      <span style={labelStyle}>Gross Margin</span>
                      <span style={{ fontWeight: '600' }}>{formatPercent(metrics.grossMargin)}</span>
                    </div>
                    <div style={metricRowStyle}>
                      <span style={labelStyle}>Operating Margin</span>
                      <span style={{ fontWeight: '600' }}>{formatPercent(metrics.operatingMargin)}</span>
                    </div>
                    <div style={metricRowStyle}>
                      <span style={labelStyle}>Net Margin</span>
                      <span style={{ fontWeight: '600' }}>{formatPercent(metrics.netMargin)}</span>
                    </div>
                    <div style={metricRowStyle}>
                      <span style={labelStyle}>Free Cash Flow Margin</span>
                      <span style={{ fontWeight: '600' }}>{formatPercentNonNegative(metrics.fcfMargin)}</span>
                    </div>
                  </div>
                </div>

                {/* Right Column: Valuation + ROIC */}
                <div>
                  <div id="valuation-section" style={{ position: 'relative' }}>
                    <h3
                      style={{
                        fontFamily: 'Manrope, sans-serif',
                        fontWeight: 600,
                        fontStyle: 'normal',
                        fontSize: '16px',
                        lineHeight: '1',
                        letterSpacing: '0%',
                        color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
                        marginBottom: '0.75rem',
                      }}
                    >
                      Valuation & Other Metrics
                    </h3>
                    <button
                      style={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        background: 'transparent',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        outline: 'none',
                        boxShadow: 'none',
                        opacity: savingBalanceSheetBookmark ? 0.6 : 1,
                        transition: 'background-color 120ms ease',
                      }}
                      title="Bookmark"
                      disabled={savingBalanceSheetBookmark}
                      onClick={saveBalanceSheetBookmark}
                    >
                      <img
                        src={bookmarkFlashById['balance-sheet'] ? '/images/bookmark_fill.svg' : (isDarkMode ? '/images/darkmode/bookmark_empy.svg' : '/images/bookmark_empy.svg')}
                        alt="Bookmark"
                        style={{
                          width: '20px',
                          height: '20px',
                        }}
                      />
                    </button>
                    <div style={metricRowStyle}>
                      <span style={labelStyle}>Market Cap</span>
                      <span style={{ fontWeight: '600' }}>{formatNumber(metrics.marketCap)}</span>
                    </div>
                    <div style={metricRowStyle}>
                      <span style={labelStyle}>Volume (Average 20 Days)</span>
                      <span style={{ fontWeight: '600' }}>
                        {Number.isFinite(metrics.volume)
                          ? Number(metrics.volume).toLocaleString(undefined, {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            })
                          : '-'}
                      </span>
                    </div>
                    <div style={metricRowStyle}>
                      <span style={labelStyle}>P/E</span>
                      <span style={{ fontWeight: '600' }}>{formatPeRatio(metrics.peRatio)}</span>
                    </div>
                    <div style={metricRowStyle}>
                      <span style={labelStyle}>Price / OCF</span>
                      <span style={{ fontWeight: '600' }}>{formatRatioNonNegative(metrics.priceToOCF)}</span>
                    </div>
                    <div style={metricRowStyle}>
                      <span style={labelStyle}>Price / FCF</span>
                      <span style={{ fontWeight: '600' }}>{formatRatioNonNegative(metrics.priceToFCF)}</span>
                    </div>
                    <div style={metricRowStyle}>
                      <span style={labelStyle}>ROIC</span>
                      <span style={{ fontWeight: '600' }}>{formatRoicPercent(metrics.roic)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Company Description */}
          {hasCompanyDescription ? (
            <div id="company-description-section" style={descriptionSectionStyle}>
              <h2 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.75rem', color: isDarkMode ? '#F9FAFB' : '#0D0D0D' }}>
                Company Description
                {showSourceLabels && descriptionSourceLabel ? (
                  <span style={sourceLabelStyle}>{descriptionSourceLabel}</span>
                ) : null}
              </h2>
              <div
                style={descriptionHtmlStyle}
                dangerouslySetInnerHTML={{ __html: descriptionHtml }}
              />
            </div>
          ) : null}

          {/* New Note Button */}
          <SummaryNewNote
            selectedStock={selectedStock}
            isDarkMode={isDarkMode}
            authToken={authToken}
            authUserId={authUserId}
            onUnauthorized={onUnauthorized}
            onNotify={onNotify}
          />
        </>
      )}

      {/* Modal for fullscreen chart */}
      {isModalOpen && (
        <div 
          id="chart-modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to bottom, rgba(107, 114, 128, 0.95), rgba(31, 41, 55, 0.95))',
            zIndex: 10000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '2rem'
          }}
          onClick={() => setIsModalOpen(false)}
        >
          <div 
            id="chart-modal-container"
            style={{
              width: '1070px',
              height: '619px',
              backgroundColor: isDarkMode ? '#111827' : '#ffffff',
              border: `1px solid ${isDarkMode ? '#374151' : 'transparent'}`,
              borderRadius: '0.5rem',
              padding: '2rem',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              id="chart-modal-close-button"
              onClick={() => setIsModalOpen(false)}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'none',
                border: 'none',
                fontSize: '2rem',
                cursor: 'pointer',
                color: '#6b7280',
                fontWeight: 'bold',
                padding: '0.5rem',
                lineHeight: '1',
                zIndex: 10001
              }}
              title="Close"
            >
              ×
            </button>

            {/* Chart title */}
            <div id="chart-modal-title" style={{ marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600', color: isDarkMode ? '#F9FAFB' : '#0D0D0D' }}>Stock Price</h2>
                {rangeReturnInfo && (
                  <>
                    <span
                      style={{
                        fontSize: '0.875rem',
                        fontWeight: '700',
                        color: rangeReturnInfo.isPositive ? '#10b981' : '#ef4444',
                      }}
                    >
                      {rangeReturnInfo.label}
                    </span>
                    <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#10b981' }}>
                      {rangeReturnInfo.aboveLowLabel}
                    </span>
                    <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#ef4444' }}>
                      {rangeReturnInfo.belowHighLabel}
                    </span>
                  </>
                )}
              </div>
            </div>

            <div style={{ ...timeRangeStyle, marginTop: '0.25rem', marginBottom: '1rem' }}>
              {['5D', '1M', '3M', 'YTD', '1Y', '3Y', '5Y', '10Y', 'All'].map(range => (
                <button
                  key={range}
                  style={timeButtonStyle(range === selectedTimeRange)}
                  onClick={() => setSelectedTimeRange(range)}
                >
                  {range}
                </button>
              ))}
            </div>

            {/* Chart container */}
            <div id="chart-modal-chart-container" style={{ flex: 1, minHeight: 0 }}>
              {chartData && (
                <Line
                  data={{
                    labels: chartData.labels,
                    datasets: [{
                      ...chartData.datasets[0],
                      fill: true,
                      pointRadius: 0,
                      pointHoverRadius: 4,
                      pointHoverBackgroundColor: '#0D0D0D',
                      pointHoverBorderColor: '#0D0D0D',
                      backgroundColor: (context: any) => {
                        const chart = context.chart;
                        const {ctx, chartArea} = chart;
                        if (!chartArea) return 'rgba(47, 88, 242, 0.3)';
                        const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                        gradient.addColorStop(0, '#2E55E9');
                        gradient.addColorStop(1, 'rgba(46, 85, 233, 0)');
                        return gradient;
                      },
                    }]
                  }}
                  plugins={[summaryCrosshairPlugin]}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      x: {
                        type: 'time',
                        time: {
                          unit: xTimeUnit as any,
                          ...(isAllRange ? { stepSize: 1 } : {}),
                          displayFormats: {
                            day: 'MMM d',
                            week: 'MMM d',
                            month: 'MMM yyyy',
                            quarter: 'MMM yyyy',
                            year: 'yyyy'
                          }
                        },
                        bounds: 'data',
                        grid: {
                          display: false,
                        },
                        ticks: {
                          color: isDarkMode ? '#F9FAFB' : '#6b7280',
                          maxRotation: 0,
                          minRotation: 0,
                          source: 'auto' as const,
                          autoSkip: isAllRange ? false : true,
                          autoSkipPadding: isAllRange ? 0 : 20,
                        },
                        ...(chartBounds ? { min: chartBounds.min, max: chartBounds.max } : {}),
                      },
                      y: {
                        position: 'right' as const,
                        beginAtZero: false,
                        grid: {
                          color: isDarkMode ? '#4B5563' : '#e5e7eb',
                        },
                        ticks: {
                          color: isDarkMode ? '#F9FAFB' : '#6b7280',
                          callback: function(value) {
                            return '$' + (typeof value === 'number' ? value.toFixed(2) : value);
                          }
                        }
                      }
                    },
                    plugins: ({
                      summaryCrosshair: {
                        enabled: true,
                        color: isDarkMode ? 'rgba(148,163,184,0.55)' : 'rgba(100,116,139,0.55)',
                        dash: [5, 5],
                        width: 1,
                      },
                      legend: {
                        display: false,
                      },
                      tooltip: {
                        mode: 'index',
                        intersect: false,
                        enabled: false,
                        external: renderStockPriceTooltip,
                      }
                    } as any),
                    interaction: {
                      mode: 'index',
                      intersect: false
                    }
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      <DataSourceAttribution isDarkMode={isDarkMode} />
    </div>
  );
};

export default ResearchSummaryScreen;
